import { MediaWidget, MediaSize } from 'models/ListViewProduct';
import { Fragment, useState, useEffect } from 'react';
import cn from 'classnames';
import { BigProductMedia } from './bigProductMedia';
import styles from './styles.scss';
import { ProductMediaThumbnail } from './productMediaThumbnail';
import Modal from 'components/Shared/Modal';
import Carousel from 'components/Shared/Carousel';
import { IProductMedia } from 'models/IProductMedia';
import Image from 'components/Shared/Image';
import connect from 'components/Shared/connect';
import { isImage, getFullProductId, isVideo } from 'views/ProductPage/selector';
import { SliderDots } from 'components/SliderDots';
import { useSelector } from 'react-redux';
import { isMobile as isMobileSelector } from 'components/Shared/responsive';

export interface OwnProps {
  classNames?: Partial<Record<'thumbnails' | 'thumbnail', string>>;
}

interface MergedProps extends OwnProps, MediaWidget {
  productId: string;
}

interface MediaState {
  showImageModal: boolean;
  selectedMediaId: string;
}

const MODAL_IMAGE_SIZE = {
  width: 1500,
  height: 1500
};

const MOBILE_IMAGE_SIZE = {
  width: 414,
  height: 414
};

const startAtIndex = (media: IProductMedia[], selectedId: string) => {
  return media.findIndex(({ id }) => id === selectedId);
};

const getCurrentMedia = (media: IProductMedia[], selectedId: string) => {
  return media.find(m => m.id === selectedId) || media[0];
};

const MediaComponent = ({ media, productId, classNames = {}, size }: MergedProps) => {
  const [state, setState] = useState<MediaState>({
    showImageModal: false,
    selectedMediaId: ''
  });
  // Store the initial size in state to prevent variants with different image sizes
  // making the product info section jump around. We do not need to update it,
  // It resets when one navigates to a different product
  const [initialMediaSize] = useState<MediaSize>(() => size);

  const isMobile = useSelector(isMobileSelector);

  useEffect(() => {
    const [first] = media;
    setState(state => ({
      ...state,
      selectedMediaId: first.id
    }));
  }, [media, productId]);

  const openModal = () =>
    setState(state => ({
      ...state,
      showImageModal: true
    }));
  const closeModal = () =>
    setState(state => ({
      ...state,
      showImageModal: false
    }));

  const setCurrentMedia = (media: IProductMedia) => {
    setState(state => ({
      ...state,
      selectedMediaId: media.id
    }));
  };

  const currentMedia = getCurrentMedia(media, state.selectedMediaId);
  const productImages = media.filter(isImage);
  const productVideos = media.filter(isVideo);
  const allMedia = [...productImages, ...productVideos];

  return (
    <Fragment>
      {!isMobile ? (
        <div className={cn(styles.media, styles.desktop)}>
          <BigProductMedia
            productId={productId}
            onClick={openModal}
            media={currentMedia}
            size={initialMediaSize}
          />
          {media.length > 1 && (
            <div className={cn(classNames.thumbnails, styles.mediaThumbnails)}>
              {media.map(media => (
                <ProductMediaThumbnail
                  className={classNames.thumbnail}
                  onClick={setCurrentMedia}
                  media={media}
                  key={media.id}
                />
              ))}
            </div>
          )}
          <Modal
            contentClassName={styles.imageModal}
            onClose={closeModal}
            show={state.showImageModal}
          >
            <Carousel
              slidesData={productImages}
              className={styles.mediaCarousel}
              displayControls
              autoFocus
              startAtIndex={startAtIndex(media, state.selectedMediaId)}
              classNames={{
                tabs: styles.mediaCarouselTabs,
                controls: styles.slideControls,
                slides: styles.slides
              }}
              onChange={setCurrentMedia}
              RenderSlideTab={SliderDots}
            >
              {(slide: IProductMedia) => (
                <Image
                  {...MODAL_IMAGE_SIZE}
                  {...slide}
                  wrapperClassName={styles.productImageLazyWrapper}
                  paddingBottom
                />
              )}
            </Carousel>
          </Modal>
        </div>
      ) : (
        <div className={cn(styles.media, styles.mobile)} key={productId}>
          <Carousel
            slidesData={allMedia}
            className={styles.mediaCarousel}
            classNames={{
              tabs: styles.mediaCarouselTabs,
              slides: styles.slides
            }}
            RenderSlideTab={SliderDots}
          >
            {(slide: IProductMedia, index) => (
              <Image
                lazy={index !== 0}
                {...MOBILE_IMAGE_SIZE}
                {...slide}
                wrapperClassName={styles.productImageLazyWrapper}
                paddingBottom
              />
            )}
          </Carousel>
        </div>
      )}
    </Fragment>
  );
};

export const Media = connect<MergedProps, MediaWidget & OwnProps>(state => ({
  productId: getFullProductId(state.currentPage)
}))(MediaComponent);
